/*
 * Project: bellcloud
 * File: apps/frontend/src/connections/modes/SinglePacketMode.ts
 * Description: 단일 패킷 통신 모드 (일반 요청-응답)
 */

import type {
  CommunicationMode,
  CommunicationContext,
  CommunicationResult,
  DeviceAdapter,
  PacketApiAdapter,
} from './types';

/**
 * 단일 패킷 통신 모드
 *
 * 사용 케이스:
 * - 일반 설정 읽기/쓰기
 * - 디바이스 정보 조회
 * - 단일 명령 실행
 *
 * 통신 흐름:
 * 1. 단일 패킷 모드 활성화
 * 2. 요청 패킷 전송
 * 3. 응답 패킷 1개 수신
 * 4. 모드 복귀
 */
export class SinglePacketMode implements CommunicationMode {
  readonly name = 'single' as const;

  private readonly packetApi: PacketApiAdapter;
  private readonly savePacketCallback?: (serialNumber: string, packet: number[]) => void;

  constructor(
    packetApi: PacketApiAdapter,
    savePacketCallback?: (serialNumber: string, packet: number[]) => void
  ) {
    this.packetApi = packetApi;
    this.savePacketCallback = savePacketCallback;
  }

  async execute(
    adapter: DeviceAdapter,
    context: CommunicationContext
  ): Promise<CommunicationResult> {
    const { deviceInfo, dto } = context;

    try {
      // 1. 단일 패킷 모드 활성화
      adapter.setSinglePacketMode();

      // 2. 버퍼 클리어 (이전 데이터 제거)
      try {
        adapter.clearBuffer(false);
      } catch (error) {
        // 버스트 모드 중이면 무시
        if (error instanceof Error && error.message.includes('버스트 모드')) {
          console.warn(`⚠️ [SinglePacketMode] 버스트 모드 중 clearBuffer 차단됨`);
        } else {
          throw error;
        }
      }

      // 3. 패킷 생성
      const packetResponse = await this.packetApi.generatePacket({
        ...dto,
        deviceInfo: deviceInfo || undefined,
      });

      if (!packetResponse || !Array.isArray(packetResponse.packet)) {
        throw new Error('패킷 생성 실패');
      }

      // [임시 진단] TX/RX 패킷 로깅. 원인 파악 후 제거.
      const __toHex = (bytes: ArrayLike<number>) =>
        Array.from(bytes)
          .map(b => b.toString(16).padStart(2, '0'))
          .join(' ');

      // 4. 패킷 전송
      const txPacket = Uint8Array.from(packetResponse.packet);
      console.log('🔎 [TX]', {
        classId: txPacket[4],
        functionId: txPacket[5],
        length: txPacket.length,
        hex: __toHex(txPacket),
      });
      await adapter.write(txPacket);

      // 5. 응답 수신 (단일 패킷)
      const responsePacket = await adapter.read();

      // 6. 패킷에서 실제 class_id, function_id 추출
      const actualClassId = responsePacket[4];
      const actualFunctionId = responsePacket[5];

      console.log('🔎 [RX]', {
        classId: actualClassId,
        functionId: actualFunctionId,
        length: responsePacket.length,
        hex: __toHex(responsePacket),
      });

      // 7. 패킷 파싱
      const jsonResponse = await this.packetApi.parsePacket({
        class_id: actualClassId,
        function_id: actualFunctionId,
        packet: Array.from(responsePacket),
        deviceInfo: deviceInfo || undefined,
      });

      const rawPacket = Array.from(responsePacket);

      // 8. 패킷 저장 (백그라운드).
      // 영속성(device info/config)은 백엔드 savePacket이 raw 패킷을 파싱해 처리하므로
      // 여기서는 raw 패킷만 보낸다. deviceInfo가 없는 최초 기기정보 읽기(class 1/func 2)도
      // 응답 json의 serial_number로 저장되도록 한다.
      const serialForSave =
        deviceInfo?.serial_number ??
        (jsonResponse.json as { serial_number?: string } | undefined)?.serial_number;
      if (serialForSave && this.savePacketCallback) {
        this.savePacketCallback(serialForSave, rawPacket);
      }

      return {
        success: true,
        response: { ...jsonResponse, packet: rawPacket },
        headerPacket: responsePacket,
      };
    } catch (error) {
      // 에러 발생 시에도 모드 복귀
      adapter.setSinglePacketMode();

      return {
        success: false,
        response: { json: {} },
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }
}
